import {Injectable} from '@angular/core';
import {
  CustomerContact,
  SigningPackage, SigningPackageFile, SigningPackageFileSearch, SigningPackageFileService,
  SigningPackageSearch,
  SigningPackageService,
  SigningPackageStatus,
  SigningPackageType,
  SimplePaginationUtils,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {distinctUntilChanged, filter, map, publishReplay, refCount, switchMap} from 'rxjs/operators';
import {CustomerContextService} from './customer-context-service';
import {isSameRef} from './utils';

@Injectable({
  providedIn: 'root',
})
export class PendingSigningPackageService {

  private pendingSigningPackage$: Observable<SigningPackage | null>;
  private reloadSource$ = new BehaviorSubject<boolean>(false);

  constructor(
    private customerContextService: CustomerContextService,
    private signingPackageService: SigningPackageService,
    private signingPackageFileService: SigningPackageFileService,
  ) {
    const distinctCustomerContacts$ = this.customerContextService.activeCustomerContact$.pipe(
      distinctUntilChanged(isSameRef),
    );
    this.pendingSigningPackage$ = combineLatest([distinctCustomerContacts$, this.reloadSource$]).pipe(
      switchMap(r => this.findActiveDraftForCustomerContact$(r[0])),
      publishReplay(1), refCount(),
    );
  }

  reload() {
    this.reloadSource$.next(true);
  }

  getPendingDraft$(signingPackageType?: SigningPackageType) {
    return this.pendingSigningPackage$.pipe(
      filter(d => d == null || signingPackageType == null || d.packageType === signingPackageType),
      publishReplay(1), refCount(),
    );
  }

  getSignerUrl(signingPackage: SigningPackage): string | null {
    const activeContactRef = this.customerContextService.getActiveContactRef();
    const signerSignUrl = this.signingPackageService.getSignerSignUrl(signingPackage, activeContactRef);
    return signingPackage.signersRedirectUri || signerSignUrl;
  }

  private findActiveDraftForCustomerContact$(customerContact: CustomerContact | null): Observable<SigningPackage | null> {
    if (customerContact == null) {
      return of(null);
    }
    const fileSearch: SigningPackageFileSearch = {
      signerContactSearch: {
        exactContactRef: customerContact.contactRef,
      },
      signingPackageSearch: {
        customerSearch: {
          exactCustomerRef: customerContact.customerRef,
        },
        anyStatusList: [SigningPackageStatus.WAIITING_SIGNATURE],
      },
    };
    const pagination = SimplePaginationUtils.newPagination(1);

    const results$ = of(null).pipe(
      switchMap(() => this.signingPackageFileService.searchSigningPackageFiles$(fileSearch, pagination)),
      map(r => r.count === 0 ? null : r.list[0]),
      switchMap((file: SigningPackageFile) => file == null ? of(null) : this.signingPackageService.getSigningPackage$(file.signingPackageRef)),
    );
    return results$;
  }

}
